import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import QRCode from 'react-qr-code';
import validate from 'validate.js';
import {
  AUTH_BACKUP_CODE_MESSAGE,
  BACKUP_CODES_NOT_AVAILABLE,
  CONFIRMATION_BUTTON_BACK,
  CONFIRMATION_BUTTON_NEXT,
  ENTER_OTP_BACKUP_CODE_SUBTITLE,
  ENTER_OTP_BACKUP_CODE_TITLE,
  ENTER_OTP_SUBTITLE,
  ENTER_OTP_TITLE,
  ERROR_BUTTON_BACK,
  ERROR_BUTTON_NEXT,
  ERROR_INSIDE,
  HIDE_KEY,
  INVALID_OTP_ERROR,
  KEY_COPIED,
  MESSAGE_BOX_TITLE_ERROR,
  MESSAGE_BOX_TITLE_SUCCESS,
  MISSING_BACKUP_CODE,
  PLEASE_GENERATE_NEW_BACKUP_CODES,
  REMEMBER_OTP_LABEL,
  RESULT_ERROR,
  RESULT_SUCCESS,
  SCALAR_OTP_COOKIE,
  SETUP_2FA_STEP_1,
  SETUP_2FA_STEP_2,
  SETUP_2FA_STEP_3,
  SETUP_2FA_SUBTITLE,
  SETUP_2FA_TITLE,
  SHOW_KEY,
  SUCCESS_INSIDE,
} from 'common/constants/login';
import { ERROR_OCCURRED } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { BackupCodes, MessageBox } from 'components';
import { GenerateBackupCodesButton } from 'pages/User2FA';
import { AuthService } from 'services';
import { useResponse } from 'services/hooks';
import { useGetUserInfo, useLogOutUser } from 'services/hooks/auth';
import AuthenticationBackupCodesInput from '../AuthenticationBackupCodesInput';
import AuthenticationInput from '../AuthenticationInput';
import {
  MessageBoxState,
  OTPCookie,
  OTPProps,
  OTPResponse,
  OtpTitles,
  OtpTitlesProps,
  OTPUriParams,
} from './OTP.props';
import {
  ActionButtons,
  CancelButton,
  CheckboxLabel,
  ErrorButton,
  KeyCopiedMsg,
  NextButton,
  NotWorkingButton,
  RootPaper,
  ShowHideOtpKeyButton,
  Steps,
  Subtitle,
} from './OTP.styles';
import { constraints, constraintsBkCode } from './OTP.validation';
import { AuthenticationInputFormState } from '../AuthenticationInput/AuthenticationInput.props';

const OTP = ({
  userHas2FA,
  verifyOtp,
  setDeviceData,
  showActionButtons = true,
  setDisplayReauthenticate,
}: OTPProps) => {
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const logOutUser = useLogOutUser();
  const getUserInfo = useGetUserInfo();
  const { processErrorResponse } = useResponse();

  const [formState, setFormState] = useState<AuthenticationInputFormState>({
    isValid: false,
    values: {},
    errors: {},
    touched: {},
  });

  const defaultMessageBoxState: MessageBoxState = {
    show: false,
    title: MESSAGE_BOX_TITLE_SUCCESS,
    result: RESULT_SUCCESS,
    backBtnText: CONFIRMATION_BUTTON_BACK,
    nextBtnText: CONFIRMATION_BUTTON_NEXT,
    type: SUCCESS_INSIDE,
    success: true,
    actionBack: () => logOutUser(),
    actionNext: () => verifyOtp?.(true),
  };

  const [otpData, setOtpData] = useState<OTPResponse>();
  const [otpSecretKey, setOtpSecretKey] = useState<string>();
  const [showOtpKey, setShowOtpKey] = useState<boolean>(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState<boolean>(false);
  const [rememberOtp, setRememberOtp] = useState<boolean>(false);
  const [messageBoxData, setMessageBoxData] = useState<MessageBoxState>(defaultMessageBoxState);
  const [isCodeWorking, setIsCodeWorking] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setFormState(formValues => ({
      ...formValues,
      values: {
        [event.target.name]: event.target.value,
      },
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setRememberOtp(event.target.checked);
  };

  const verifyBackupCode = useCallback(async () => {
    const { backupCode } = formState.values;
    if (!backupCode) {
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          backupCode: MISSING_BACKUP_CODE,
        },
      }));
      return;
    }
    const authService = AuthService.getInstance();
    try {
      const response = await authService.verifyBackupCode(backupCode as string);
      if (response) {
        verifyOtp?.(true);
        localStorage.setItem('isOtpVerified', 'true');
      }
    } catch (error: any) {
      const errorMsg = JSON.parse(JSON.stringify(error.response?.body?.token ?? ERROR_OCCURRED));
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          backupCode: [errorMsg],
        },
      }));
    }
  }, [formState.values, verifyOtp]);

  const refreshBackupTokens = async () => {
    setIsGeneratingBackupCodes(true);

    const authService = AuthService.getInstance();

    try {
      const response = await authService.createBackupCodes({
        user_has_totp_device: true,
        confirmed: true,
      });

      if (response) {
        getUserInfo();
        setBackupCodes(response.tokens);
        setOpenModal(true);
      }
    } catch (error: any) {
      // If we recieve a Forbidden error we need to reauthenticate the user
      if (error?.status === 403 && setDisplayReauthenticate) {
        setDisplayReauthenticate(true);
      }
    }

    setIsGeneratingBackupCodes(false);
  };

  const validateOtp = async ({ showMessageBox }: { showMessageBox: boolean }) => {
    const authService = AuthService.getInstance();

    try {
      const response = await authService.verifyOTP(formState.values.otp as string);

      if (response?.verified) {
        setMessageBoxData({
          ...defaultMessageBoxState,
          show: showMessageBox,
        });

        if (setDeviceData) {
          setDeviceData(prevDeviceData => ({
            ...prevDeviceData,
            confirmed: true,
          }));

          await refreshBackupTokens();
        }

        if (!showMessageBox) {
          verifyOtp?.(true);
          localStorage.setItem('isOtpVerified', 'true');
        }

        try {
          const sanitizedUserId = Number.parseInt(user?.id?.toString() ?? '', 10);
          if (Number.isNaN(sanitizedUserId)) {
            throw new Error('Invalid user ID');
          }
          if (rememberOtp && sanitizedUserId) {
            // Remember cookie for 30 days
            document.cookie = `${SCALAR_OTP_COOKIE}_${sanitizedUserId}=true; \
            SameSite=Strict; \
            path=/; expires=${new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 30).toUTCString()}`;
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      } else {
        setFormState(prevFormState => ({
          ...prevFormState,
          errors: {
            otp: [INVALID_OTP_ERROR],
          },
        }));
      }
    } catch (error: any) {
      const errorMsg = JSON.parse(JSON.stringify(error.response?.body?.detail ?? ERROR_OCCURRED));
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          otp: [errorMsg],
        },
      }));
    }
  };

  const cookieToObject = (cookie: string): OTPCookie => {
    const cookieObj: OTPCookie = {};
    cookie.split('; ').forEach(c => {
      const [key, value] = c.split('=');
      // The cookie value is stored as a string, so we need to convert it to a boolean
      cookieObj[key.trim()] = value === 'true';
    });
    return cookieObj;
  };

  const getOtpUriParams = (uri: string): OTPUriParams => {
    const params: OTPUriParams = {};
    const paramStrings = uri.split('?')[1].split('&');
    paramStrings.forEach(param => {
      const pairArr = param.split('=');
      const pairKey = pairArr[0].toLowerCase();
      const pairVal = pairArr[1];
      params[pairKey] = pairVal;
    });
    return params;
  };

  useEffect(() => {
    const generateOrReturnUserTOTP = async () => {
      try {
        const authService = AuthService.getInstance();
        const response = await authService.createTOTP();
        setOtpData(response);
        const otpUriParams = getOtpUriParams(response.url);
        setOtpSecretKey(otpUriParams?.secret ?? '');
      } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Unknown error');
      }
    };

    if (!userHas2FA) {
      generateOrReturnUserTOTP();
    }

    return () => {
      setOtpData(undefined);
      setOtpSecretKey(undefined);
    };
  }, [userHas2FA]);

  useEffect(() => {
    if (otpData && setDeviceData) {
      const { persistent_id } = otpData;
      setDeviceData(prevDeviceData => ({
        ...prevDeviceData,
        persistent_id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpData]);

  useEffect(() => {
    const errors = validate(formState.values, !isCodeWorking ? constraints : constraintsBkCode);
    setFormState(formCurrentState => ({
      ...formCurrentState,
      isValid: !errors,
      errors: errors ?? {},
    }));
  }, [formState.values, isCodeWorking]);

  const otpCookie = useMemo(() => {
    if (document.cookie && user?.id) {
      const scalarCookie
        = document.cookie.split('; ').find(row => row.startsWith(`${SCALAR_OTP_COOKIE}_${user.id}`)) ?? '';
      return cookieToObject(scalarCookie);
    }
    return { scalar_otp: false };
  }, [user]);

  useEffect(() => {
    // Skip the OTP check (bypass 2FA and go straight to the firm selector)
    // if the cookie is present for the current userand verifyOTP is available
    if (user?.id && otpCookie[`${SCALAR_OTP_COOKIE}_${user.id}`] && verifyOtp) {
      verifyOtp(true);
    }
  }, [otpCookie, verifyOtp, user]);

  const getOtpTitles = ({ useBackupCode }: OtpTitlesProps): OtpTitles => {
    if (useBackupCode) {
      return {
        title: ENTER_OTP_BACKUP_CODE_TITLE,
        subtitle: ENTER_OTP_BACKUP_CODE_SUBTITLE,
      };
    }
    return {
      title: ENTER_OTP_TITLE,
      subtitle: ENTER_OTP_SUBTITLE,
    };
  };

  const handleClose = (_: unknown, reason?: string) => {
    if (reason && reason === 'backdropClick') {
      return;
    }
    setOpenModal(false);
  };

  useEffect(() => {
    if (copiedToClipboard) {
      const timer = setTimeout(() => {
        setCopiedToClipboard(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [copiedToClipboard]);

  const showOrHideOtpKey = () => {
    setShowOtpKey(prev => !prev);
  };

  const handleCopyToClipboard = async () => {
    try {
      if (otpSecretKey) {
        await navigator.clipboard.writeText(otpSecretKey);
        setCopiedToClipboard(true);
      }
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  if (userHas2FA && !user?.has_tokens_2fa) {
    return (
      <RootPaper>
        <Typography variant="h3">{BACKUP_CODES_NOT_AVAILABLE}</Typography>
        <Subtitle>{PLEASE_GENERATE_NEW_BACKUP_CODES}</Subtitle>
        <Grid container justifyContent="center" spacing={2} component={ActionButtons}>
          <Grid item>
            <GenerateBackupCodesButton
              className="generate-backup-codes-button"
              generateNewBackupCodes={refreshBackupTokens}
              disabled={false}
            />
          </Grid>
        </Grid>
      </RootPaper>
    );
  }

  if (userHas2FA) {
    return (
      <>
        <BackupCodes
          backupCodes={backupCodes}
          handleClose={handleClose}
          handleGenerateNewCodes={refreshBackupTokens}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          isModal
          openModal={openModal}
          username={user?.email}
        />
        <RootPaper>
          <Typography variant="h3">{getOtpTitles({ useBackupCode: isCodeWorking }).title}</Typography>
          <Subtitle>{getOtpTitles({ useBackupCode: isCodeWorking }).subtitle}</Subtitle>
          {!isCodeWorking ? (
            <AuthenticationInput formState={formState} handleChange={handleInputChange} />
          ) : (
            <>
              <AuthenticationBackupCodesInput formState={formState} handleChange={handleInputChange} />
              <CheckboxLabel>{AUTH_BACKUP_CODE_MESSAGE}</CheckboxLabel>
            </>
          )}
          {!isCodeWorking && (
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={rememberOtp} onChange={handleCheckboxChange} />}
                label={<CheckboxLabel>{REMEMBER_OTP_LABEL}</CheckboxLabel>}
              />
            </FormGroup>
          )}
          <Grid container justifyContent="flex-end" spacing={2} component={ActionButtons}>
            <Grid item>
              <CancelButton variant="contained" color="primary" onClick={logOutUser}>
                Back
              </CancelButton>
            </Grid>
            <Grid item>
              <NextButton
                variant="contained"
                onClick={() => (!isCodeWorking ? validateOtp({ showMessageBox: false }) : verifyBackupCode())}
                disabled={isEmpty(formState.values) || !formState.isValid}>
                Next
              </NextButton>
            </Grid>
          </Grid>
        </RootPaper>
        {!isCodeWorking && (
          <NotWorkingButton variant="contained" color="primary" onClick={() => setIsCodeWorking(true)}>
            CODE NOT WORKING?
          </NotWorkingButton>
        )}
      </>
    );
  }

  return (
    <>
      {otpData && !messageBoxData.show && (
        <RootPaper>
          <Typography variant="h3">{SETUP_2FA_TITLE}</Typography>
          <Subtitle>{SETUP_2FA_SUBTITLE}</Subtitle>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <QRCode value={otpData.url} size={150} />
            </Grid>
            <Grid item xs={8}>
              <Typography>{SETUP_2FA_STEP_1}</Typography>
              <ShowHideOtpKeyButton onClick={showOrHideOtpKey}>
                {!showOtpKey ? SHOW_KEY : HIDE_KEY}
              </ShowHideOtpKeyButton>
              {showOtpKey && <Steps onClick={handleCopyToClipboard}>{otpSecretKey}</Steps>}
              {copiedToClipboard && <KeyCopiedMsg>{KEY_COPIED}</KeyCopiedMsg>}
              <Steps>{SETUP_2FA_STEP_2}</Steps>
              <Steps>{SETUP_2FA_STEP_3}</Steps>
            </Grid>
          </Grid>
          <AuthenticationInput formState={formState} handleChange={handleInputChange} />
          <Grid container justifyContent="flex-end" spacing={2} component={ActionButtons}>
            <Grid item>
              <CancelButton
                id="cancel-btn"
                variant="contained"
                color="primary"
                onClick={() => {
                  setMessageBoxData({
                    show: true,
                    title: MESSAGE_BOX_TITLE_ERROR,
                    result: RESULT_ERROR,
                    backBtnText: ERROR_BUTTON_BACK,
                    nextBtnText: ERROR_BUTTON_NEXT,
                    type: ERROR_INSIDE,
                    success: false,
                    actionBack: () => verifyOtp?.(true),
                    actionNext: () => setMessageBoxData({ ...defaultMessageBoxState, show: false }),
                  });
                }}>
                Cancel
              </CancelButton>
            </Grid>
            <Grid item>
              <NextButton
                variant="contained"
                onClick={() => validateOtp({ showMessageBox: true })}
                disabled={isEmpty(formState.values) || !formState.isValid}>
                Enable
              </NextButton>
            </Grid>
          </Grid>
        </RootPaper>
      )}
      {messageBoxData.show && (
        <RootPaper>
          <Typography variant="h3">{messageBoxData.title}</Typography>
          <MessageBox
            title=""
            tagline={`Two-Factor Authentication ${messageBoxData.result}`}
            hasFrame={false}
            hasBgColor
            fullWidth={false}
            type={messageBoxData.type}
          />
          <Grid container justifyContent="flex-end" spacing={2} component={ActionButtons}>
            {showActionButtons && (
              <>
                <Grid item>
                  <CancelButton variant="contained" color="primary" onClick={messageBoxData.actionBack}>
                    {messageBoxData.backBtnText}
                  </CancelButton>
                </Grid>
                <Grid item>
                  {messageBoxData.success ? (
                    <NextButton variant="contained" onClick={messageBoxData.actionNext}>
                      {messageBoxData.nextBtnText}
                    </NextButton>
                  ) : (
                    <ErrorButton variant="contained" onClick={messageBoxData.actionNext}>
                      {messageBoxData.nextBtnText}
                    </ErrorButton>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </RootPaper>
      )}
    </>
  );
};

export default OTP;
